/**
 * useScopedStorageKey — Namespaces a localStorage key per identity so a
 * signed-in user's local data (to-do list, family checklist, …) can never be
 * read while browsing as a guest, and vice versa.
 *
 * Guests always get the `__guest` bucket, which starts empty for every new
 * guest session unless that guest created data themselves.
 */
import { useAuth } from "@/hooks/useAuth";

export const GUEST_SCOPE = "guest";

export function scopedKey(base: string, userId?: string | null) {
  return `${base}__${userId ?? GUEST_SCOPE}`;
}

export function useScopedStorageKey(base: string) {
  const { user } = useAuth();
  return scopedKey(base, user?.id ?? null);
}

/**
 * Removes every user-scoped local key for the given user (called on sign-out).
 * Guest buckets are untouched.
 */
export function clearScopedStorageForUser(userId?: string | null) {
  if (typeof window === "undefined" || !userId) return;
  const suffix = `__${userId}`;
  const toRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key && key.endsWith(suffix)) toRemove.push(key);
  }
  toRemove.forEach((k) => window.localStorage.removeItem(k));
}

/** Removes every user-scoped local key (any identity except the guest bucket). */
export function clearAllUserScopedStorage() {
  if (typeof window === "undefined") return;
  const toRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key && /__[0-9a-fA-F-]{36}$/.test(key)) toRemove.push(key);
  }
  toRemove.forEach((k) => window.localStorage.removeItem(k));
}
